/**
 * LevelUpModal component - handles level up confirmation and point assignment.
 * Uses direct point value assignment (click a point value, then click a stat/skill).
 */

import { useState } from 'react';
import { router } from '@inertiajs/react';
import type { Character } from '@/types/characters';
import { STAT_KEYS, SKILL_KEYS, STAT_LABELS, SKILL_LABELS, MAX_STAT_VALUE } from '@/types/characters';

interface LevelUpModalProps {
    /** The character to level up */
    character: Character;
    /** Callback to close the modal */
    onClose: () => void;
}

type Step = 'confirm' | 'assign-stats' | 'assign-skills' | 'complete';

/**
 * Modal for leveling up a character and assigning bonus points.
 * Point assignment uses direct value selection: click a point value (+2, +3, etc.)
 * then click on a stat/skill to assign it directly.
 */
export function LevelUpModal({ character, onClose }: LevelUpModalProps) {
    const [step, setStep] = useState<Step>(
        character.can_level_up ? 'confirm' :
        character.pending_stat_points ? 'assign-stats' :
        character.pending_skill_points ? 'assign-skills' : 'complete'
    );
    const [isProcessing, setIsProcessing] = useState(false);
    // Track which stat/skill each point value is assigned to (key = stat/skill name, value = point amount)
    const [statAllocations, setStatAllocations] = useState<Record<string, number>>({});
    const [skillAllocations, setSkillAllocations] = useState<Record<string, number>>({});
    // Track which point values have been assigned (index in the pending points array)
    const [assignedStatPointIndices, setAssignedStatPointIndices] = useState<Set<number>>(new Set());
    const [assignedSkillPointIndices, setAssignedSkillPointIndices] = useState<Set<number>>(new Set());
    // Currently selected point value to assign (index in the pending points array)
    const [selectedStatPointIndex, setSelectedStatPointIndex] = useState<number | null>(null);
    const [selectedSkillPointIndex, setSelectedSkillPointIndex] = useState<number | null>(null);

    // Handle level up confirmation
    const handleLevelUp = () => {
        setIsProcessing(true);
        router.post(
            `/character/${character.id}/level-up`,
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    setStep('assign-stats');
                    setIsProcessing(false);
                },
                onError: () => {
                    setIsProcessing(false);
                },
            }
        );
    };

    // Handle stat point assignment
    const handleAssignStats = () => {
        setIsProcessing(true);
        router.post(
            `/character/${character.id}/assign-stat-points`,
            { allocations: statAllocations },
            {
                preserveScroll: true,
                onSuccess: () => {
                    setStep('assign-skills');
                    setIsProcessing(false);
                },
                onError: () => {
                    setIsProcessing(false);
                },
            }
        );
    };

    // Handle skill point assignment
    const handleAssignSkills = () => {
        setIsProcessing(true);
        router.post(
            `/character/${character.id}/assign-skill-points`,
            { allocations: skillAllocations },
            {
                preserveScroll: true,
                onSuccess: () => {
                    setStep('complete');
                    setIsProcessing(false);
                    // Close modal after a short delay
                    setTimeout(onClose, 1500);
                },
                onError: () => {
                    setIsProcessing(false);
                },
            }
        );
    };

    // Get available points to assign
    const pendingStatPoints = character.pending_stat_points || [];
    const pendingSkillPoints = character.pending_skill_points || [];

    // Check if all points have been assigned
    const allStatPointsAssigned = assignedStatPointIndices.size === pendingStatPoints.length;
    const allSkillPointsAssigned = assignedSkillPointIndices.size === pendingSkillPoints.length;

    /**
     * Assign a stat point value to a stat.
     * If the stat already has a point assigned, swap the assignments.
     */
    const handleStatClick = (stat: string) => {
        if (selectedStatPointIndex === null) return;

        const pointValue = pendingStatPoints[selectedStatPointIndex];
        const currentValue = character.stats[stat]?.value || 0;
        const existingAllocation = statAllocations[stat] || 0;

        // Check if adding this point would exceed max
        if (currentValue + pointValue > MAX_STAT_VALUE) return;

        // If this stat already has a point assigned, unassign it first
        if (existingAllocation > 0) {
            // Find which index was assigned to this stat and unassign it
            const previousIndex = pendingStatPoints.findIndex((val, idx) =>
                val === existingAllocation && assignedStatPointIndices.has(idx)
            );
            if (previousIndex !== -1) {
                setAssignedStatPointIndices(prev => {
                    const next = new Set(prev);
                    next.delete(previousIndex);
                    return next;
                });
            }
        }

        // Assign the selected point to this stat
        setStatAllocations(prev => ({ ...prev, [stat]: pointValue }));
        setAssignedStatPointIndices(prev => new Set(prev).add(selectedStatPointIndex));
        setSelectedStatPointIndex(null);
    };

    /**
     * Unassign a stat point from a stat.
     */
    const handleStatUnassign = (stat: string) => {
        const allocation = statAllocations[stat];
        if (!allocation) return;

        // Find the index of this point value in the assigned set
        const indexToRemove = Array.from(assignedStatPointIndices).find(idx =>
            pendingStatPoints[idx] === allocation
        );

        if (indexToRemove !== undefined) {
            setAssignedStatPointIndices(prev => {
                const next = new Set(prev);
                next.delete(indexToRemove);
                return next;
            });
        }

        setStatAllocations(prev => {
            const next = { ...prev };
            delete next[stat];
            return next;
        });
    };

    /**
     * Assign a skill point value to a skill.
     */
    const handleSkillClick = (skill: string) => {
        if (selectedSkillPointIndex === null) return;

        const pointValue = pendingSkillPoints[selectedSkillPointIndex];
        const currentValue = character.skills[skill]?.value || 0;
        const existingAllocation = skillAllocations[skill] || 0;

        // Check if adding this point would exceed max
        if (currentValue + pointValue > MAX_STAT_VALUE) return;

        // If this skill already has a point assigned, unassign it first
        if (existingAllocation > 0) {
            const previousIndex = pendingSkillPoints.findIndex((val, idx) =>
                val === existingAllocation && assignedSkillPointIndices.has(idx)
            );
            if (previousIndex !== -1) {
                setAssignedSkillPointIndices(prev => {
                    const next = new Set(prev);
                    next.delete(previousIndex);
                    return next;
                });
            }
        }

        // Assign the selected point to this skill
        setSkillAllocations(prev => ({ ...prev, [skill]: pointValue }));
        setAssignedSkillPointIndices(prev => new Set(prev).add(selectedSkillPointIndex));
        setSelectedSkillPointIndex(null);
    };

    /**
     * Unassign a skill point from a skill.
     */
    const handleSkillUnassign = (skill: string) => {
        const allocation = skillAllocations[skill];
        if (!allocation) return;

        const indexToRemove = Array.from(assignedSkillPointIndices).find(idx =>
            pendingSkillPoints[idx] === allocation
        );

        if (indexToRemove !== undefined) {
            setAssignedSkillPointIndices(prev => {
                const next = new Set(prev);
                next.delete(indexToRemove);
                return next;
            });
        }

        setSkillAllocations(prev => {
            const next = { ...prev };
            delete next[skill];
            return next;
        });
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-white">
                        {step === 'confirm' && 'Level Up!'}
                        {step === 'assign-stats' && 'Assign Stat Points'}
                        {step === 'assign-skills' && 'Assign Skill Points'}
                        {step === 'complete' && 'Level Up Complete!'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                    >
                        ✕
                    </button>
                </div>

                {/* Confirm Step */}
                {step === 'confirm' && (
                    <div className="text-center">
                        <p className="text-gray-300 mb-4">
                            {character.name} is ready to level up to <strong>Level {character.level + 1}</strong>!
                        </p>
                        <p className="text-gray-400 text-sm mb-6">
                            You will receive a random +2 bonus to one stat and one skill,
                            plus 7 points each to distribute to stats and skills.
                        </p>
                        <button
                            onClick={handleLevelUp}
                            disabled={isProcessing}
                            className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded disabled:opacity-50"
                        >
                            {isProcessing ? 'Leveling Up...' : 'Confirm Level Up'}
                        </button>
                    </div>
                )}

                {/* Assign Stats Step - Direct point value assignment */}
                {step === 'assign-stats' && (
                    <div>
                        <p className="text-gray-300 mb-2">
                            Click a point value below, then click a stat to assign it.
                        </p>

                        {/* Point value buttons - click to select, then click a stat */}
                        <div className="flex gap-2 mb-4 flex-wrap">
                            {pendingStatPoints.map((points, index) => {
                                const isAssigned = assignedStatPointIndices.has(index);
                                const isSelected = selectedStatPointIndex === index;

                                return (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            if (isAssigned) return;
                                            setSelectedStatPointIndex(isSelected ? null : index);
                                        }}
                                        disabled={isAssigned}
                                        className={`px-4 py-2 rounded font-bold transition-all ${
                                            isAssigned
                                                ? 'bg-green-600/50 text-green-300 cursor-not-allowed'
                                                : isSelected
                                                ? 'bg-yellow-500 text-black ring-2 ring-yellow-300'
                                                : 'bg-blue-600 hover:bg-blue-500 text-white'
                                        }`}
                                    >
                                        +{points}
                                        {isAssigned && ' ✓'}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Instruction text when a point is selected */}
                        {selectedStatPointIndex !== null && (
                            <p className="text-yellow-400 text-sm mb-3">
                                Now click a stat below to assign +{pendingStatPoints[selectedStatPointIndex]}
                            </p>
                        )}

                        {/* Stats list - click to assign selected point */}
                        <div className="space-y-2 mb-6">
                            {STAT_KEYS.map((stat) => {
                                const currentValue = character.stats[stat]?.value || 0;
                                const allocated = statAllocations[stat] || 0;
                                const hasAllocation = allocated > 0;
                                const canAssign = selectedStatPointIndex !== null &&
                                    currentValue + pendingStatPoints[selectedStatPointIndex] <= MAX_STAT_VALUE;

                                return (
                                    <div
                                        key={stat}
                                        onClick={() => canAssign && handleStatClick(stat)}
                                        className={`flex items-center justify-between p-3 rounded transition-all ${
                                            canAssign
                                                ? 'bg-gray-700 hover:bg-blue-600/50 cursor-pointer'
                                                : hasAllocation
                                                ? 'bg-green-900/30 border border-green-600/50'
                                                : 'bg-gray-700/50'
                                        }`}
                                    >
                                        <span className="text-gray-300">{STAT_LABELS[stat]}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400">{currentValue}%</span>
                                            {hasAllocation && (
                                                <>
                                                    <span className="text-green-400 font-bold">+{allocated}</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatUnassign(stat);
                                                        }}
                                                        className="w-6 h-6 bg-red-600/50 hover:bg-red-600 rounded text-white text-sm"
                                                        title="Remove assignment"
                                                    >
                                                        ✕
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            onClick={handleAssignStats}
                            disabled={isProcessing || !allStatPointsAssigned}
                            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded disabled:opacity-50"
                        >
                            {isProcessing ? 'Assigning...' : 'Confirm Stat Points'}
                        </button>
                    </div>
                )}

                {/* Assign Skills Step - Direct point value assignment */}
                {step === 'assign-skills' && (
                    <div>
                        <p className="text-gray-300 mb-2">
                            Click a point value below, then click a skill to assign it.
                        </p>

                        {/* Point value buttons - click to select, then click a skill */}
                        <div className="flex gap-2 mb-4 flex-wrap">
                            {pendingSkillPoints.map((points, index) => {
                                const isAssigned = assignedSkillPointIndices.has(index);
                                const isSelected = selectedSkillPointIndex === index;

                                return (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            if (isAssigned) return;
                                            setSelectedSkillPointIndex(isSelected ? null : index);
                                        }}
                                        disabled={isAssigned}
                                        className={`px-4 py-2 rounded font-bold transition-all ${
                                            isAssigned
                                                ? 'bg-green-600/50 text-green-300 cursor-not-allowed'
                                                : isSelected
                                                ? 'bg-yellow-500 text-black ring-2 ring-yellow-300'
                                                : 'bg-blue-600 hover:bg-blue-500 text-white'
                                        }`}
                                    >
                                        +{points}
                                        {isAssigned && ' ✓'}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Instruction text when a point is selected */}
                        {selectedSkillPointIndex !== null && (
                            <p className="text-yellow-400 text-sm mb-3">
                                Now click a skill below to assign +{pendingSkillPoints[selectedSkillPointIndex]}
                            </p>
                        )}

                        {/* Skills list - click to assign selected point */}
                        <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                            {SKILL_KEYS.map((skill) => {
                                const currentValue = character.skills[skill]?.value || 0;
                                const allocated = skillAllocations[skill] || 0;
                                const hasAllocation = allocated > 0;
                                const canAssign = selectedSkillPointIndex !== null &&
                                    currentValue + pendingSkillPoints[selectedSkillPointIndex] <= MAX_STAT_VALUE;

                                return (
                                    <div
                                        key={skill}
                                        onClick={() => canAssign && handleSkillClick(skill)}
                                        className={`flex items-center justify-between p-3 rounded transition-all ${
                                            canAssign
                                                ? 'bg-gray-700 hover:bg-blue-600/50 cursor-pointer'
                                                : hasAllocation
                                                ? 'bg-green-900/30 border border-green-600/50'
                                                : 'bg-gray-700/50'
                                        }`}
                                    >
                                        <span className="text-gray-300">{SKILL_LABELS[skill]}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-400">{currentValue}%</span>
                                            {hasAllocation && (
                                                <>
                                                    <span className="text-green-400 font-bold">+{allocated}</span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSkillUnassign(skill);
                                                        }}
                                                        className="w-6 h-6 bg-red-600/50 hover:bg-red-600 rounded text-white text-sm"
                                                        title="Remove assignment"
                                                    >
                                                        ✕
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            onClick={handleAssignSkills}
                            disabled={isProcessing || !allSkillPointsAssigned}
                            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded disabled:opacity-50"
                        >
                            {isProcessing ? 'Assigning...' : 'Confirm Skill Points'}
                        </button>
                    </div>
                )}

                {/* Complete Step */}
                {step === 'complete' && (
                    <div className="text-center">
                        <div className="text-6xl mb-4">🎉</div>
                        <p className="text-gray-300 mb-4">
                            {character.name} is now <strong>Level {character.level}</strong>!
                        </p>
                        <p className="text-gray-400 text-sm">
                            Closing automatically...
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LevelUpModal;

